<script setup lang="ts">
import type { DataTableHeader } from 'vuetify'
import type { CarePlanListItem } from '@/views/apps/nursing/types'
import { useCarePlanList, resolveStatusVariant, resolveStatusLabel, formatDate, isReviewOverdue, deleteCarePlan, exportCarePlansCsv } from '@/views/apps/nursing/useCarePlan'

// DAY-37: only for users whose roles allow it (same permission the API checks)
definePage({
  meta: {
    action: 'read',
    subject: 'CarePlans',
  },
})

const { t } = useI18n()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const selectedRows = ref<number[]>([])
const searchQuery = ref('')
const selectedFilter = ref()

// Update data table options
const updateOptions = (options: any) => {
  if (options.sortBy[0]) {
    sortBy.value = options.sortBy[0].key
    orderBy.value = options.sortBy[0].order
  }
}

// Avoid stranding the user on a page that no longer exists after the visible
// row count changes.
watch([selectedFilter, searchQuery, itemsPerPage], () => {
  page.value = 1
})

// Headers
const headers: DataTableHeader[] = [
  { title: 'Patient', key: 'memberFirstName' },
  { title: 'Care Plan', key: 'title' },
  { title: 'Start Date', key: 'startAt' },
  { title: 'Review Date', key: 'reviewAt' },
  { title: 'Status', key: 'status' },
  { title: 'Care Plan ID', key: 'id', sortable: false, width: 120 } as DataTableHeader,
  { title: 'Actions', key: 'actions', sortable: false, align: 'end', width: 56 } as DataTableHeader,
]

// Fetch care plans from API
const { carePlansData, fetchCarePlans, isFetching, error, statusCode } = await useCarePlanList()

const apiError = computed(() => {
  if (!error.value)
    return null
  if (statusCode.value === 401)
    return 'Authentication failed. Please sign in again.'

  return `API error (${statusCode.value || 'network'}): ${error.value}`
})

const carePlans = computed(() => {
  if (!carePlansData.value)
    return []

  let items: CarePlanListItem[] = Array.isArray(carePlansData.value) ? carePlansData.value : []

  // Status / overdue filter
  if (selectedFilter.value) {
    if (selectedFilter.value === 'overdue')
      items = items.filter(cp => isReviewOverdue(cp.reviewAt, cp.status))
    else
      items = items.filter(cp => cp.status.toLowerCase() === selectedFilter.value)
  }

  // Search filter — applies on top of the status filter
  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    items = items.filter(cp =>
      `${cp.memberFirstName} ${cp.memberLastName}`.toLowerCase().includes(q)
      || (cp.title ?? '').toLowerCase().includes(q)
      || cp.status.toLowerCase().includes(q),
    )
  }

  return items
})

const totalCarePlans = computed(() => carePlans.value.length)

// For csv export. Ids of the currently displayed rows. Has to reapply sort to the columns.
const viewIds = () => {
  const rows = [...carePlans.value]
  const k = sortBy.value as keyof CarePlanListItem | undefined
  if (k) {
    const dir = orderBy.value === 'desc' ? -1 : 1
    rows.sort((a, b) => {
      const x = a[k] ?? ''
      const y = b[k] ?? ''

      // Check whether key is number first. Currently all sortable columns are Strings.
      if (typeof x === 'number' && typeof y === 'number')
        return dir * (x - y)

      return dir * String(x).localeCompare(String(y), undefined, { sensitivity: 'accent' })
    })
  }
  return rows.map(r => r.id)
}

const snackbar = useSnackbar()
const isExporting = ref(false)

const runExport = async() => {
  isExporting.value = true
  try {
    await exportCarePlansCsv(viewIds())
  }
  catch (err: any) {
    snackbar.error(err?.statusCode === 403
      ? 'You do not have permission to export care plans.'
      : 'Export failed. Please try again.')
  }
  finally {
    isExporting.value = false
  }
}

// Filters
const filters = [
  { title: 'Active Plans', value: 'active' },
  { title: 'Draft Plans', value: 'draft' },
  { title: 'Inactive Plans', value: 'inactive' },
  { title: 'Completed Plans', value: 'completed' },
  { title: 'Overdue Plans', value: 'overdue' },
]

const widgetData = computed(() => {
  const all = Array.isArray(carePlansData.value) ? carePlansData.value : [] as CarePlanListItem[]
  const activeCount = all.filter((cp: CarePlanListItem) => cp.status === 'active').length
  const overdueCount = all.filter((cp: CarePlanListItem) => isReviewOverdue(cp.reviewAt, cp.status)).length
  const completedCount = all.filter((cp: CarePlanListItem) => cp.status === 'completed').length

  return [
    { title: 'Total Plans', value: all.length, desc: 'Total Care Plans', icon: 'bx-file', iconColor: 'primary' },
    { title: 'Active Plans', value: activeCount, desc: 'Currently Active', icon: 'bx-check-circle', iconColor: 'success' },
    { title: 'Overdue', value: overdueCount, desc: 'Review Overdue', icon: 'bx-error-circle', iconColor: 'error' },
    { title: 'Completed', value: completedCount, desc: 'Completed Plans', icon: 'bx-badge-check', iconColor: 'info' },
  ]
})

const isAddNewCarePlanDrawerVisible = ref(false)

// Delete-confirmation state. Mirrors the users-list pattern: opening the
// dialog only stages the id; nothing changes server-side until Yes is clicked.
const isDeleteDialogVisible = ref(false)
const carePlanToDeleteId = ref<number | null>(null)

const promptDeleteCarePlan = (id: number) => {
  carePlanToDeleteId.value = id
  isDeleteDialogVisible.value = true
}

const cancelDeleteCarePlan = () => {
  isDeleteDialogVisible.value = false
  carePlanToDeleteId.value = null
}

const confirmDeleteCarePlan = async () => {
  const id = carePlanToDeleteId.value
  isDeleteDialogVisible.value = false
  carePlanToDeleteId.value = null
  if (id == null) return

  await deleteCarePlan(id)

  const idx = selectedRows.value.findIndex(row => row === id)
  if (idx !== -1) selectedRows.value.splice(idx, 1)

  fetchCarePlans()
}
</script>

<template>
  <section>
    <!-- Widgets -->
    <div class="d-flex mb-6">
      <VRow>
        <template v-for="(data, id) in widgetData" :key="id">
          <VCol cols="12" md="3" sm="6">
            <VCard>
              <VCardText>
                <div class="d-flex justify-space-between">
                  <div class="d-flex flex-column gap-y-1">
                    <div class="text-body-1 text-high-emphasis">
                      {{ data.title }}
                    </div>
                    <div class="d-flex gap-x-2 align-center">
                      <h4 class="text-h4">
                        {{ data.value }}
                      </h4>
                    </div>
                    <div class="text-sm">
                      {{ data.desc }}
                    </div>
                  </div>
                  <VAvatar :color="data.iconColor" variant="tonal" rounded size="40">
                    <VIcon :icon="data.icon" size="24" />
                  </VAvatar>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </template>
      </VRow>
    </div>

    <!-- API Error Alert -->
    <VAlert
      v-if="apiError"
      type="error"
      variant="tonal"
      class="mb-6"
      closable
    >
      {{ apiError }}
      <template #append>
        <VBtn
          size="small"
          variant="outlined"
          color="error"
          @click="fetchCarePlans()"
        >
          Retry
        </VBtn>
      </template>
    </VAlert>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('Filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- Select Filter -->
          <VCol cols="12" sm="4">
            <AppSelect v-model="selectedFilter" placeholder="Select Filter" :items="filters" clearable clear-icon="bx-x" />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: 'All' },
            ]"
            style="inline-size: 6.25rem"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- Search -->
          <div style="inline-size: 15.625rem">
            <AppTextField v-model="searchQuery" placeholder="Search Care Plan" />
          </div>

          <VBtn variant="tonal" color="secondary" prepend-icon="bx-export" :loading="isExporting" @click="runExport">
            Export
          </VBtn>

          <!-- Add care plan button -->
          <VBtn prepend-icon="bx-plus" @click="isAddNewCarePlanDrawerVisible = true">
            Add New Care Plan
          </VBtn>
        </div>
      </VCardText>
      <VDivider />

      <!-- Data table -->
      <VDataTable
        v-model:items-per-page="itemsPerPage"
        v-model:model-value="selectedRows"
        v-model:page="page"
        :items="carePlans"
        item-value="id"
        :headers="headers"
        class="text-no-wrap"
        show-select
        :loading="isFetching"
        @update:options="updateOptions"
      >
        <!-- Patient -->
        <template #item.memberFirstName="{ item }">
          <div
            class="d-flex align-center gap-x-4"
          >
            <VAvatar size="34" variant="tonal" color="primary">
              <span>{{ (item.memberFirstName?.[0] ?? '') + (item.memberLastName?.[0] ?? '') }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                <RouterLink
                  :to="{ name: 'apps-nursing-care-plans-id', params: { id: item.id } }"
                  class="font-weight-medium text-link"
                >
                  {{ item.memberFirstName }} {{ item.memberLastName }}
                </RouterLink>
              </h6>
              <div class="text-sm text-disabled">
                DOB: {{ item.memberDateOfBirth ? formatDate(item.memberDateOfBirth) : 'N/A' }}
              </div>
            </div>
          </div>
        </template>

        <!-- Care Plan Title -->
        <template #item.title="{ item }">
          <h6 class="text-base">
            <RouterLink
              :to="{ name: 'apps-nursing-care-plans-id', params: { id: item.id } }"
              class="font-weight-medium text-link"
            >
              {{ item.title || 'Untitled Plan' }}
            </RouterLink>
          </h6>
          <div class="text-sm text-disabled text-capitalize">
            {{ item.intent }}
          </div>
        </template>

        <!-- Start Date -->
        <template #item.startAt="{ item }">
          <div
            class="text-body-1 text-high-emphasis"
          >
            {{ formatDate(item.startAt) }}
          </div>
          <div v-if="item.endAt" class="text-sm text-disabled">
            to {{ formatDate(item.endAt) }}
          </div>
        </template>

        <!-- Review Date -->
        <template #item.reviewAt="{ item }">
          <div
            class="text-body-1"
            :class="{
              'text-error font-weight-bold': isReviewOverdue(item.reviewAt, item.status),
              'text-high-emphasis': !isReviewOverdue(item.reviewAt, item.status),
            }"
          >
            {{ formatDate(item.reviewAt) }}
            <VIcon
              v-if="isReviewOverdue(item.reviewAt, item.status)"
              icon="bx-error-circle"
              size="16"
              color="error"
              class="ms-1"
            />
          </div>
          <div
            v-if="isReviewOverdue(item.reviewAt, item.status)"
            class="text-sm text-error"
          >
            Overdue
          </div>
        </template>

        <!-- ID -->
        <template #item.id="{ item }">
          <div
            class="text-body-1 text-high-emphasis"
          >
            {{ '#' + item.id }}
          </div>
        </template>

        <!-- Status -->
        <template #item.status="{ item }">
          <VChip :color="resolveStatusVariant(item.status)" size="small" label>
            {{ resolveStatusLabel(item.status) }}
          </VChip>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <VBtn icon variant="text" color="medium-emphasis">
            <VIcon icon="bx-dots-vertical-rounded" />
            <VMenu activator="parent">
              <VList>
                <VListItem :to="{ name: 'apps-nursing-care-plans-id', params: { id: item.id } }">
                  <template #prepend>
                    <VIcon icon="bx-show" />
                  </template>
                  <VListItemTitle>{{ t('View') }}</VListItemTitle>
                </VListItem>

                <VListItem :to="{ name: 'apps-nursing-care-plans-id', params: { id: item.id }, query: { edit: 'all' } }">
                  <template #prepend>
                    <VIcon icon="bx-pencil" />
                  </template>
                  <VListItemTitle>{{ t('Edit') }}</VListItemTitle>
                </VListItem>

                <VListItem @click="promptDeleteCarePlan(item.id)">
                  <template #prepend>
                    <VIcon icon="bx-trash" color="error" />
                  </template>
                  <VListItemTitle class="text-error">
                    {{ t('Delete') }}
                  </VListItemTitle>
                </VListItem>
              </VList>
            </VMenu>
          </VBtn>
        </template>

      </VDataTable>
    </VCard>

    <!-- 👉 Inactivate confirmation -->
    <VDialog
      v-model="isDeleteDialogVisible"
      max-width="480"
      @update:model-value="(v: boolean) => { if (!v) cancelDeleteCarePlan() }"
    >
      <VCard>
        <VCardTitle class="d-flex align-center pe-2">
          {{ t('Confirm Inactivation') }}
          <VSpacer />
          <VBtn icon variant="text" size="small" @click="cancelDeleteCarePlan">
            <VIcon icon="bx-x" />
          </VBtn>
        </VCardTitle>

        <VDivider />

        <VCardText class="pt-4">
          {{ t('Are you sure you want to Inactivate this Care Plan?') }}
        </VCardText>

        <VCardActions class="px-4 pb-4">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" @click="cancelDeleteCarePlan">
            {{ t('No') }}
          </VBtn>
          <VBtn color="error" @click="confirmDeleteCarePlan">
            {{ t('Yes') }}
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </section>
</template>
